import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';

// Couleurs Avengers
const avengersColors = {
  red: '#a50000',
  blue: '#3268bd',
  orange: '#ff9500',
  purple: '#460061',
  green: '#00991f'
};

type SubMenuItem = {
  title: string;
  description: string;
  icon: string;
  isImage?: boolean;
  path?: string;
};

type NavItem = {
  label: string;
  href: string;
  description: string;
  submenu?: SubMenuItem[];
  isNew?: boolean;
};

const navigationItems: NavItem[] = [
  {
    label: "Solutions",
    href: "solutions",
    description: "Technologies d'avant-garde",
    submenu: [
      {
        title: "RAID 0 Neuronal By Mr D.",
        description: "IA distribuée ultra-performante",
        icon: "/images/edge-ai-icon.png",
        isImage: true,
        path: "/EdgeIntelligence"
      },
      {
        title: "Genius Framework",
        description: "Apprentissage adaptatif",
        icon: "/images/genius-ai-icon.png",
        isImage: true,
        path: "/GeniusFramework"
      },
      {
        title: "StradivariApp",
        description: "Expression musicale IA",
        icon: "/images/music-ai-icon.png",
        isImage: true,
        path: "/StradivariApp"
      },
      {
        title: "AstroDaily",
        description: "Horoscope quotidien avec IA",
        icon: "🌟",
        isImage: false,
        path: "/astrodaily"
      }
    ]
  },
  {
    label: "Swiss Avengers",
    href: "swiss-avengers",
    description: "L'élite de l'IA suisse",
    submenu: [
      {
        title: "Infrastructure de pointe",
        description: "Notre environnement technologique",
        icon: "/images/swiss-shield-icon.png",
        isImage: true,
        path: "/swiss-avengers-saga"
      },
      {
        title: "L'équipe d'élite",
        description: "Experts en IA et éthique",
        icon: "/images/swiss-helmet-icon.png",
        isImage: true
      }
    ]
  },
  {
    label: "Impact",
    href: "impact",
    description: "Notre contribution",
    submenu: [
      {
        title: "Innovation Locale",
        description: "Infrastructure optimisée pour l'IA haute performance",
        icon: "/images/local-innovation-icon.png",
        isImage: true
      },
      {
        title: "Transformation Digitale",
        description: "Accompagnement des entreprises dans leur transformation numérique",
        icon: "/images/digital-impact-icon.png",
        isImage: true
      },
      {
        title: "Impact Durable",
        description: "Solutions éco-responsables et durables",
        icon: "/images/sustainable-impact-icon.png",
        isImage: true
      },
      {
        title: "Showcase",
        description: "Projets d'innovation",
        icon: "/images/showcase-icon.png",
        isImage: true
      },
      {
        title: "Modular Tech",
        description: "Architecture flexible",
        icon: "/images/component-icon.png",
        isImage: true
      },
      {
        title: "Intelligence",
        description: "Solutions IA avancées",
        icon: "/images/ai-brain-icon.png",
        isImage: true
      },
      {
        title: "Robotique",
        description: "Assistance à la marche",
        icon: "/images/assistive-walk-icon.png",
        isImage: true
      }
    ]
  },
  {
    label: "Blog",
    href: "/blog",
    description: "Articles et innovations"
  },
  {
    label: "Portail Client",
    href: "/portail-client",
    description: "Accès sécurisé aux documents"
  },
  {
    label: "Live",
    href: "/live",
    description: "TV & Podcast streaming"
  },
  {
    label: "Music",
    href: "/music",
    description: "940 pièces sur SoundCloud"
  }
];

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const menuRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const handleNavigation = (href: string, e: React.MouseEvent, path?: string) => {
    e.preventDefault();
    setOpenMenu(null);
    
    if (path) {
      navigate(path);
      return;
    }
    
    // Gérer les liens externes (URLs absolues)
    if (href.startsWith('http://') || href.startsWith('https://')) {
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }
    
    if (href.startsWith('/')) {
      navigate(href);
    } else {
      if (location.pathname !== '/') {
        navigate('/', { state: { scrollTo: href } });
      } else {
        const element = document.getElementById(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (openMenu && menuRefs.current[openMenu] && !menuRefs.current[openMenu]?.contains(e.target as Node)) {
      setOpenMenu(null);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenu]);

  return (
    <nav className="fixed top-0 left-0 right-0 z-40" style={{
      background: 'rgba(255, 255, 255, 0.8)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
    }}>
      <div className="container mx-auto px-6 py-2">
        <div className="flex items-center justify-between">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="hardware-accelerated"
          >
            <Link to="/" onClick={(e) => { e.preventDefault(); navigate('/'); }}>
              <img src="/images/logo.png" alt="Infinity Cloud" className="h-12 w-auto" style={{ display: 'block', objectFit: 'contain' }} />
            </Link>
          </motion.div>
          
          <div className="hidden md:flex items-center space-x-8">
            {navigationItems.map((item) => (
              <div 
                key={item.label} 
                className="relative"
                ref={el => menuRefs.current[item.label] = el}
              >
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    if (item.submenu) {
                      setOpenMenu(openMenu === item.label ? null : item.label);
                    } else {
                      handleNavigation(item.href, e);
                    }
                  }}
                  className="py-2 px-3 rounded-full font-bold inline-flex items-center transition-colors"
                  style={{
                    background: 'transparent !important',
                    border: 'none'
                  }}
                >
                  <span
                    className="relative"
                    style={{
                      color: '#000000 !important',
                      fontSize: '1.25rem !important',
                      WebkitTextFillColor: '#000000 !important',
                      background: 'transparent !important',
                      backgroundImage: 'none !important',
                      backgroundClip: 'unset !important',
                      WebkitBackgroundClip: 'unset !important'
                    }}
                  >
                    {item.label}
                    {item.isNew && (
                      <span 
                        className="absolute -top-3 -right-8 text-xs font-bold px-1.5 py-0.5 rounded-full animate-pulse"
                        style={{
                          background: 'linear-gradient(135deg, #ff0080, #ff8c00)',
                          color: 'white',
                          fontSize: '10px'
                        }}
                      >
                        NEW
                      </span>
                    )}
                  </span>
                  {item.submenu && (
                    <motion.svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 ml-1 opacity-80 transform transition-all duration-300"
                      animate={{ rotate: openMenu === item.label ? 180 : 0 }}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      style={{
                        color: '#000000'
                      }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </motion.svg>
                  )}
                </button>
                
                <AnimatePresence>
                  {item.submenu && openMenu === item.label && (
                    <motion.div 
                      className="absolute top-full left-1/2 transform -translate-x-1/2 pt-2 z-50"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      style={{ display: 'block', visibility: 'visible', opacity: 1 }}
                    >
                      <div 
                        className="dropdown-menu p-2 rounded-2xl min-w-[300px] grid gap-1"
                        style={{
                          maxHeight: item.label === "Impact" ? '400px' : 'auto',
                          overflowY: item.label === "Impact" ? 'auto' : 'visible',
                          background: 'white',
                          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
                        }}
                      >
                        {item.submenu.map((subitem, index) => (
                          <motion.button
                            key={subitem.title}
                            onClick={(e) => handleNavigation(item.href, e, subitem.path)}
                            className="dropdown-item flex items-center space-x-3 p-2.5 rounded-xl w-full"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ 
                              opacity: 1, 
                              y: 0,
                              transition: { delay: index * 0.05 }
                            }}
                            whileHover={{ 
                              backgroundColor: 'rgba(0, 0, 0, 0.03)',
                              transition: { duration: 0.2 }
                            }}
                          >
                            {subitem.isImage ? (
                              <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center overflow-hidden bg-gray-50">
                                <img 
                                  src={subitem.icon} 
                                  alt={subitem.title} 
                                  className="w-8 h-8 object-contain"
                                  style={{ display: 'block', visibility: 'visible', opacity: 1 }}
                                />
                              </div>
                            ) : (
                              <span className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-lg"
                                style={{
                                  background: `linear-gradient(135deg, ${avengersColors.blue}, ${avengersColors.purple})`,
                                  color: 'white'
                                }}
                              >
                                {subitem.icon}
                              </span>
                            )}
                            <div className="flex-1 text-left">
                              <p className="text-base font-semibold" style={{ color: avengersColors.red }}>
                                {subitem.title}
                              </p>
                              <p className="text-xs mt-0.5" style={{ color: avengersColors.blue }}>
                                {subitem.description}
                              </p>
                            </div>
                          </motion.button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

          <Link to="/contact">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="text-white font-bold py-2 px-6 rounded-full shadow-lg"
              style={{
                 background: `linear-gradient(to right, ${avengersColors.red}, ${avengersColors.purple})`
              }}
            >
              Contact
            </motion.button>
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default Navbar; 